import { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Avatar,
  Select,
  MenuItem,
  FormControl,
  useTheme,
  useMediaQuery,
  Container,
  CircularProgress,
} from "@mui/material";
import {
  Person,
  Category,
  Inventory,
  Settings,
  CheckCircle,
  Warning,
  ArrowUpward,
  ArrowDownward,
  ReportProblem,
  PersonAdd,
  ContactSupportOutlined,
  Notifications,
} from "@mui/icons-material";
// import ContactSupportOutlinedIcon from '@mui/icons-material/ContactSupportOutlined';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import GroupsIcon from "@mui/icons-material/Groups";
import DashboardIcon from "@mui/icons-material/Dashboard";
import { useNavigate } from "react-router-dom";
import axios from "../axiosInstance";
import PageTitle from "../Components/PageTitle";

const Dashboard = () => {
  const navigate = useNavigate();
  const [timePeriod, setTimePeriod] = useState("last7days");
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    fetchDashboardData();
  }, [timePeriod]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const daysMap = {
        last7days: 7,
        last30days: 30,
        last90days: 90,
      };

      const days = daysMap[timePeriod] || 7;
      const response = await axios.get(
        `/api/v1/dashboard/super/admin?days=${days}`,
      );
      if (response.data.success) {
        setDashboardData(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const totalUsers =
    dashboardData?.totalSellers + dashboardData?.totalBuyers || 0;
  const metricCards = [
    {
      icon: <Person sx={{ color: "#3b82f6" }} />,
      title: "Total Users",
      value: totalUsers?.toString() || "0",
      color: "#eff6ff",
      route: "/admin/users",
      badge: {
        label: `${dashboardData?.activeUsers || 0} Active`,
        color: "#dbeafe",
        textColor: "#3b82f6",
      },
    },
    {
      icon: <Category sx={{ color: "#f59e0b" }} />,
      title: "Seller Categories",
      value: dashboardData?.totalSellerCategories?.toString() || "0",
      color: "#fef3c7",
      route: "/admin/seller-categories",
    },
    {
      icon: <Category sx={{ color: "#8b5cf6" }} />,
      title: "Buyer Categories",
      value: dashboardData?.totalBuyerCategories?.toString() || "0",
      color: "#f3e8ff",
      route: "/admin/buyer-categories",
    },
    {
      icon: <Inventory sx={{ color: "#10b981" }} />,
      title: "Total Products",
      value: dashboardData?.totalProducts?.toString() || "0",
      color: "#d1fae5",
      route: "/admin/seller-product",
      badge: {
        label: `${dashboardData?.newProducts || 0} New`,
        color: "#d1fae5",
        textColor: "#10b981",
      },
    },
  ];

  const quickActions = [
    {
      icon: <GroupsIcon sx={{ color: "#ffffff" }} />,
      title: "Manage Contact",
      color: "#546073",
      route: "/admin/contact",
    },
    {
      icon: <Category sx={{ color: "#ffffff" }} />,
      title: "Manage Account",
      color: "#e7bf7a",
      route: "/admin/profile",
    },
    {
      icon: <SupportAgentIcon sx={{ color: "#ffffff" }} />,
      title: "Support Tickets",
      color: "#58695e",
      route: "/admin-dashboard/support",
    },
  ];

  const getChartData = () => {
    if (!dashboardData?.graphData || dashboardData.graphData.length === 0) {
      return [
        { month: "Aug", value: 12 },
        { month: "Sep", value: 18 },
        { month: "Oct", value: 25 },
        { month: "Nov", value: 32 },
        { month: "Dec", value: 45 },
        { month: "Jan", value: 58 },
      ];
    }

    return dashboardData.graphData.map((item) => ({
      month: item.name,
      value: item.users,
    }));
  };

  const chartData = getChartData();
  const barColors = chartData.map((item, index) =>
    index === chartData.length - 1 ? "#0b4064" : "#93c5fd",
  );
  const barOpacities = chartData.map((item, index) =>
    index === chartData.length - 1 ? 1 : 0.7,
  );

  // Format Live Feed for user problems/registrations
  const getFeedItems = () => {
    return dashboardData?.liveFeed.map((item) => {
      const isProblem = item.type === "support";

      // Format time relative to now
      const getTimeAgo = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffMinutes = Math.floor(diffMs / (1000 * 60));

        if (diffDays > 0) {
          return `${diffDays} ${diffDays === 1 ? "day" : "days"} ago`;
        } else if (diffHours > 0) {
          return `${diffHours} ${diffHours === 1 ? "hour" : "hours"} ago`;
        } else if (diffMinutes > 0) {
          return `${diffMinutes} ${
            diffMinutes === 1 ? "minute" : "minutes"
          } ago`;
        } else {
          return "Just now";
        }
      };

      return {
        id: item.id,
        userName: item.name || item.userName,
        action: item.action,
        time: getTimeAgo(item.time),
        type: item.type,
        severity: item.severity,
        isNew: item.isNew,
      };
    });
  };

  const feedItems = getFeedItems();

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 17) return "Good Afternoon";
    return "Good Evening";
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case "critical":
        return "#ef4444";
      case "high":
        return "#f59e0b";
      case "medium":
        return "#3b82f6";
      default:
        return "#64748b";
    }
  };

  // const getFeedIcon = (item) => {
  //   if (item.type === "support") {
  //     return (
  //       <ReportProblem
  //         sx={{ color: getSeverityColor(item.severity), fontSize: 16 }}
  //       />
  //     );
  //   } else if (item.type === "contact") {
  //     return (
  //       <ContactSupportOutlined
  //         sx={{ color: getSeverityColor(item.severity), fontSize: 16 }}
  //       />
  //     );
  //   } else {
  //     return <PersonAdd sx={{ color: "#10b981", fontSize: 16 }} />;
  //   }
  // };

  const getFeedIcon = (item) => {
  switch (item.type) {
    case "support":
      return (
        <ReportProblem
          sx={{ color: getSeverityColor(item.severity), fontSize: 16 }}
        />
      );

    case "contact":
      return (
        <ContactSupportOutlined
          sx={{ color: "#0ea5e9", fontSize: 16 }} // Blue
        />
      );

    case "user":
      return (
        <PersonAdd
          sx={{ color: "#10b981", fontSize: 16 }} // Green
        />
      );

    default:
      return (
        <Notifications
          sx={{ color: "#6b7280", fontSize: 16 }}
        />
      );
  }
};


  const typeColors = {
    support: "#fee2e2",
    contact: "#e0f2fe",
    user: "#eafff1",
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          bgcolor: "#f8f9fa",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <PageTitle
        title="Admin Dashboard - BizBridge"
        description="Overview of platform activities and user insights"
        keywords="admin dashboard, users, categories, products, analytics"
        url="/admin-dashboard"
      />

      <Box sx={{ bgcolor: "#f8f9fa", minHeight: "100vh" }}>
        <Container maxWidth="xl" sx={{ py: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
              flexWrap: "none",
              gap: 3,
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 800, color: "#1e293b" }}>
              Admin Dashboard
            </Typography>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <Select
                value={timePeriod}
                onChange={(e) => setTimePeriod(e.target.value)}
                sx={{
                  bgcolor: "white",
                  border: "1px solid #e2e8f0",
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                }}
              >
                <MenuItem value="last7days">Last 7 Days</MenuItem>
                <MenuItem value="last30days">Last 30 Days</MenuItem>
                <MenuItem value="last90days">Last 90 Days</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Card
            sx={{
              bgcolor: "#1e293b",
              color: "white",
              mb: 3,
              p: { xs: 3, sm: 4, md: 5 },
              borderRadius: 3,
            }}
            elevation={0}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: { xs: "flex-start", md: "center" },
                flexDirection: { xs: "column", md: "row" },
                gap: 3,
              }}
            >
              <Box>
                <Typography variant="body2" sx={{ color: "#94a3b8", mb: 1 }}>
                  {getGreeting()}, Admin
                </Typography>
                <Typography
                  variant={isMobile ? "h6" : "h4"}
                  sx={{ fontWeight: "bold" }}
                >
                  User registrations are{" "}
                  <Box
                    component="span"
                    sx={{
                      color:
                        dashboardData?.userGrowth >= 0 ? "#10b981" : "#ef4444",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    {dashboardData?.userGrowth >= 0 ? (
                      <>
                        <ArrowUpward
                          sx={{ fontSize: isMobile ? "0.9rem" : "1.1rem" }}
                        />
                        up
                      </>
                    ) : (
                      <>
                        <ArrowDownward
                          sx={{ fontSize: isMobile ? "0.9rem" : "1.1rem" }}
                        />
                        down
                      </>
                    )}{" "}
                    {Math.abs(dashboardData?.businessGrowth || 15)}%
                  </Box>{" "}
                  this month.
                </Typography>
              </Box>

              <Button
                variant="contained"
                startIcon={<DashboardIcon fontSize="large" />}
                fullWidth={isMobile}
                onClick={() => navigate("/admin/pending-invoices")}
                sx={{
                  bgcolor: "white",
                  color: "#0b4064",
                  "&:hover": {
                    bgcolor: "#f1f5f9",
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  },
                  px: 5.5,
                  py: 1.5,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  transition: "all 0.3s ease",
                }}
              >
                Pending Invoices
              </Button>
            </Box>
          </Card>

          <Box sx={{ mb: { xs: 3, lg: 2 } }}>
            <Grid container spacing={3} sx={{ alignItems: "flex-start" }}>
              <Grid item xs={12} lg={8}>
                <Card
                  elevation={0}
                  sx={{
                    border: "1px solid #e2e8f0",
                    borderRadius: 2,
                    overflow: "hidden",
                    height: "fit-content",
                  }}
                >
                  <Box
                    sx={{
                      px: 3,
                      py: 2,
                      borderBottom: "1px solid #e2e8f0",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: "#1e293b",
                        fontSize: "1.1rem",
                      }}
                    >
                      Platform Overview
                    </Typography>
                  </Box>

                  <CardContent sx={{ p: 3 }}>
                    <Grid container spacing={2}>
                      {metricCards.map((card, index) => (
                        <Grid item xs={12} sm={6} md={6} key={index}>
                          <Card
                            elevation={0}
                            onClick={() => {
                              if (card.route) {
                                navigate(card.route);
                              }
                            }}
                            sx={{
                              border: "1px solid #e2e8f0",
                              height: "100%",
                              borderRadius: 2,
                              transition: "all 0.3s ease",
                              cursor: card.route ? "pointer" : "default",
                              "&:hover": {
                                boxShadow: card.route
                                  ? "0 4px 12px rgba(0,0,0,0.08)"
                                  : "none",
                                transform: card.route
                                  ? "translateY(-4px)"
                                  : "none",
                              },
                            }}
                          >
                            <CardContent sx={{ p: 2.5 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "flex-start",
                                  mb: 2,
                                }}
                              >
                                <Avatar
                                  sx={{
                                    bgcolor: card.color,
                                    width: 48,
                                    height: 48,
                                  }}
                                >
                                  {card.icon}
                                </Avatar>

                                {card.badge && (
                                  <Chip
                                    label={card.badge.label}
                                    size="small"
                                    sx={{
                                      bgcolor: card.badge.color,
                                      color: card.badge.textColor,
                                      fontWeight: 600,
                                      fontSize: "0.7rem",
                                      height: 24,
                                    }}
                                  />
                                )}
                              </Box>

                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 1, fontSize: "0.875rem" }}
                              >
                                {card.title}
                              </Typography>

                              <Typography
                                sx={{
                                  fontWeight: "bold",
                                  fontSize: "2rem",
                                  color: "#0b4064",
                                }}
                              >
                                {card.value}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} lg={4}>
                <Card
                  elevation={0}
                  sx={{
                    border: "1px solid #e2e8f0",
                    borderRadius: 2,
                    overflow: "hidden",
                    height: "fit-content",
                  }}
                >
                  <Box
                    sx={{
                      px: 3,
                      py: 2,
                      borderBottom: "1px solid #e2e8f0",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: "#1e293b",
                        fontSize: "1.1rem",
                      }}
                    >
                      Quick Actions
                    </Typography>
                  </Box>

                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                      }}
                    >
                      {quickActions.map((action, index) => (
                        <Card
                          key={index}
                          elevation={0}
                          onClick={() => navigate(action.route)}
                          sx={{
                            border: "1px solid #e2e8f0",
                            borderRadius: 2,
                            cursor: "pointer",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                              transform: "translateX(4px)",
                              borderColor: action.color,
                              bgcolor: "#f8fafc",
                            },
                          }}
                        >
                          <CardContent sx={{ p: 5.5 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                              }}
                            >
                              <Avatar
                                sx={{
                                  bgcolor: action.color,
                                  width: 44,
                                  height: 44,
                                }}
                              >
                                {action.icon}
                              </Avatar>

                              <Typography
                                variant="body1"
                                sx={{
                                  fontSize: "0.95rem",
                                  fontWeight: 600,
                                  color: "#1e293b",
                                }}
                              >
                                {action.title}
                              </Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>

          <Box>
            <Grid container spacing={3} sx={{ alignItems: "flex-start" }}>
              <Grid item xs={12} lg={8}>
                <Card
                  elevation={0}
                  sx={{
                    border: "1px solid #e2e8f0",
                    borderRadius: 2,
                    height: "fit-content",
                  }}
                >
                  <Box
                    sx={{
                      px: 3,
                      py: 1.5,
                      borderBottom: "1px solid #e2e8f0",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: "#1e293b",
                          fontSize: "1.1rem",
                        }}
                      >
                        User Registration Trend
                      </Typography>
                      <Chip
                        label="Monthly View"
                        size="small"
                        variant="outlined"
                        sx={{
                          borderColor: "#64748b",
                          fontSize: "0.75rem",
                        }}
                      />
                    </Box>
                  </Box>

                  <CardContent sx={{ p: 3 }}>
                    <ResponsiveContainer
                      width="100%"
                      height={isMobile ? 250 : 400}
                    >
                      <BarChart
                        data={chartData}
                        margin={{
                          top: 20,
                          right: isMobile ? 10 : 30,
                          left: isMobile ? -20 : 0,
                          bottom: 5,
                        }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke="#cbd5e1"
                          vertical={false}
                        />
                        <XAxis
                          dataKey="month"
                          tick={{
                            fill: "#64748b",
                            fontSize: isMobile ? 10 : 12,
                          }}
                          axisLine={{ stroke: "#cbd5e1" }}
                          tickLine={false}
                        />
                        <YAxis
                          tick={{
                            fill: "#64748b",
                            fontSize: isMobile ? 9 : 11,
                          }}
                          axisLine={{ stroke: "#cbd5e1" }}
                          tickLine={false}
                          domain={[0, "dataMax + 10"]}
                          tickFormatter={(value) => `${value}`}
                        />
                        <Tooltip
                          formatter={(value) => [
                            `${value} users`,
                            "Registrations",
                          ]}
                          contentStyle={{
                            backgroundColor: "white",
                            border: "1px solid #e2e8f0",
                            borderRadius: "8px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                            fontSize: isMobile ? "12px" : "14px",
                          }}
                          cursor={false}
                          labelFormatter={(label) => `Month: ${label}`}
                        />
                        <Bar
                          dataKey="value"
                          name="Registrations"
                          radius={[8, 8, 0, 0]}
                          maxBarSize={isMobile ? 40 : 80}
                        >
                          {chartData.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={barColors[index % barColors.length]}
                              opacity={
                                barOpacities[index % barOpacities.length]
                              }
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} lg={4}>
                {/* Live Feed Card - User Problems and Registrations */}
                <Card
                  elevation={0}
                  sx={{
                    border: "1px solid #e2e8f0",
                    borderRadius: 2,
                    height: "fit-content",
                  }}
                >
                  <Box
                    sx={{
                      px: 3,
                      py: 2,
                      borderBottom: "1px solid #e2e8f0",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: "#1e293b",
                          fontSize: "1.1rem",
                        }}
                      >
                        Activity Feed
                      </Typography>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor: "#22c55e",
                            animation: "pulse 1.5s infinite",
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#22c55e",
                            fontWeight: 500,
                            fontSize: "0.875rem",
                          }}
                        >
                          Live
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <CardContent sx={{ p: 0 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      {feedItems?.length > 0 ? (
                        feedItems.map((item, index) => (
                          <Box
                            key={item.id}
                            sx={{
                              px: 2,
                              py: 0.8,
                              borderBottom:
                                index < feedItems.length - 1
                                  ? "1px solid #f1f5f9"
                                  : "none",
                              "&:hover": {
                                bgcolor: "#f8fafc",
                              },
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                px: 3,
                                py: 1.5,
                                borderBottom: "1px solid #eef2f7",
                                "&:hover": {
                                  bgcolor: "#f8fafc",
                                  cursor: "pointer",
                                },
                              }}
                            >
                              {/* Icon */}
                              <Box
                                sx={{
                                  width: 36,
                                  height: 36,
                                  borderRadius: "50%",
                                  // bgcolor:
                                  //   item.type === "support"
                                  //     ? "#fee2e2"
                                  //     : "#eafff1",

                                  bgcolor: typeColors[item.type] || "#f3f4f6",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  mr: 2,
                                  flexShrink: 0,
                                }}
                              >
                                {getFeedIcon(item)}
                              </Box>

                              {/* User + Action */}
                              <Box sx={{ flex: 1, lineHeight: 1.1 }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontWeight: 600,
                                      fontSize: "0.95rem",
                                      color: "#0f172a",
                                    }}
                                  >
                                    {item.userName}
                                  </Typography>
                                  {item.isNew && (
                                    <Chip
                                      label="New"
                                      size="small"
                                      sx={{
                                        bgcolor: "#dbeafe",
                                        color: "#3b82f6",
                                        height: 18,
                                        fontSize: "0.65rem",
                                        fontWeight: 600,
                                      }}
                                    />
                                  )}
                                </Box>

                                <Typography
                                  sx={{
                                    fontSize: "0.8rem",
                                    color: "#6b7280",
                                    mt: 0.2,
                                  }}
                                >
                                  {item.action}
                                </Typography>
                              </Box>

                              {/* Time + Severity Badge */}
                              <Box sx={{ textAlign: "right", lineHeight: 1.2 }}>
                                {item.type === "support" && item.severity && (
                                  <Chip
                                    label={item.severity.toUpperCase()}
                                    size="small"
                                    sx={{
                                      bgcolor: `${getSeverityColor(item.severity)}15`,
                                      color: getSeverityColor(item.severity),
                                      height: 20,
                                      fontSize: "0.65rem",
                                      fontWeight: 700,
                                      mb: 0.5,
                                    }}
                                  />
                                )}

                                <Typography
                                  sx={{
                                    fontSize: "0.75rem",
                                    color: "#6b7280",
                                    mt: 0.2,
                                  }}
                                >
                                  {item.time}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        ))
                      ) : (
                        <Box
                          sx={{
                            px: 3,
                            py: 4,
                            textAlign: "center",
                          }}
                        >
                          <Typography color="text.secondary">
                            No recent activity
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Container>

        <style>
          {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
          }
        `}
        </style>
      </Box>
    </>
  );
};

export default Dashboard;
